// Top level libraries and 3rd party dependencies
import React, { Component } from 'react';
import { func, object, string } from 'prop-types';
import { contains, isEmpty } from 'ramda';
import { Link } from 'react-router-dom';

import { DEFAULT_HIPAA_SAFE_LABEL } from '../../constants/AppConstants';
import Layout from '../Layout';
import LoadingState from '../LoadingState';
import { PIN_LENGTH, MAX_RETRY_COUNT } from '../../constants/AppConstants';
import ErrorMessage from '../ErrorMessage';
import * as types from '../../constants/ActionTypes';
import Footer from '../Footer';

// Page styles
import cx from 'classnames';
import s from './Expired.css';


export default class Expired extends Component {
    static propTypes = {
        conversationHash: string.isRequired,
        loadingState: object,
        limitedLogin: func.isRequired,
        limitedLoginSendMessage: func.isRequired,
        messages: object.isRequired,
        conversationDetails: object.isRequired,
        session: object.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            input: '',
            PIN: '',
        };
    }

    // enforce 5 as maxLength with slice()
    handleChangePIN = event => this.setState({[event.target.name]: event.target.value.slice(0, PIN_LENGTH)});

    /**
     * input type=number allows for floating point numbers! 4e+3E is valid. We use this to prevent + = . e and number pad .
     * http://keycode.info/
     */
    handleFloatingPointKeys = event => {
        let keyCode =  event.keyCode;

        const isNotWanted = (keyCode === 69 || keyCode === 101 || keyCode === 187 || keyCode === 189 ||keyCode === 190 || keyCode === 110 || keyCode === 107 || keyCode === 109);

        if (isNotWanted) event.preventDefault();
    };

    // Tests will fail if we do not give this Ramda method what it expects, an array. this.props.loadingState.requests returns an array.
    isLoadingLimitedLogin = loadingState => contains(types.LIMITED_LOGIN_REQUEST, loadingState.requests || []);
    isLoadingLimitedLoginSendMessage = loadingState => contains(types.LIMITED_LOGIN_SEND_MESSAGE_REQUEST, loadingState.requests || []);

    inputHandler = event => this.setState({ input: event.target.value });

    handleLimitedLogin = e => {
        e.preventDefault();

        const { conversationHash, limitedLogin } = this.props;
        const { PIN } = this.state;

        if (this.isValidPIN()) {
            const request = {
                isExpired: true,
                conversationHash,
                PIN,
            };

            limitedLogin(request);
            e.target.blur();
        }
    };

    // The api is expecting a multi-part request. Same as /conversation/reply.
    handleLimitedLoginMessageSend = e => {
        e.preventDefault();

        const { limitedLoginSendMessage } = this.props;
        const { input } = this.state;

        if (this.isValidMessage()) {
            let formData = new FormData();
            formData.append('message', input);

            limitedLoginSendMessage(formData);
        }
    };

    isValidPIN = () => {
        const { PIN } = this.state;

        return PIN.length === PIN_LENGTH;
    };

    isValidMessage = () => {
        const { input } = this.state;

        return input !== '';
    };

    render() {
        const {
            conversationDetails,
            conversationHash,
            loadingState,
            messages,
            session,
        } = this.props;

        const { input, PIN } = this.state;

        const hipaaSafePracticeName = `${conversationDetails.practiceName}` === DEFAULT_HIPAA_SAFE_LABEL
            ? `your ` + DEFAULT_HIPAA_SAFE_LABEL : `${conversationDetails.practiceName}`;
        const hasConversationDetails = !isEmpty(conversationDetails);
        const lockedOut = session.error ? session.error.retryCount >= MAX_RETRY_COUNT : false;
        const additionalLockedOutCopy = `${conversationDetails.practiceName}` === DEFAULT_HIPAA_SAFE_LABEL
            ? `Your ${conversationDetails.practiceName} has been notified of the failure to authenticate.`
            : `${conversationDetails.practiceName} has been notified of the failure to authenticate.`;
        const additionalPracticePhoneCopy = conversationDetails.practicePhone ? ` at ${conversationDetails.practicePhone}.` : '.';
        const isValidPIN = this.isValidPIN();
        const isValidMessage = this.isValidMessage();
        const isLoadingLimitedLogin = this.isLoadingLimitedLogin(loadingState);
        const isLoadingLimitedLoginSendMessage = this.isLoadingLimitedLoginSendMessage(loadingState);
        const limitedLoginMessageSentSuccess = messages.type === types.LIMITED_LOGIN_SEND_MESSAGE_SUCCESS;
        const limitedLoginSendMessageTimerEnd = session.time && session.time.type === types.LIMITED_LOGIN_SEND_MESSAGE_TIMER_END;

        // If the user has authenticated with PIN, session.limitedLogin's state should display the message form.
        return (
            <Layout centered className={s.layout}>
                {!session.limitedLogin &&
                    <form
                        className={cx(s.component, session.error ? 'hasErrors' : '')}
                        id="limitedLogin"
                        onSubmit={this.handleLimitedLogin}
                    >
                        {hasConversationDetails && !lockedOut &&
                            <div id="conversationDetails" className={cx(s.alignItems, s.componentBlock, s.verticalSpacing, s.heading)}>
                                <h3>{conversationDetails.practiceName}</h3>
                                <label>{conversationDetails.practicePhone}</label>
                            </div>
                        }

                        {hasConversationDetails && !lockedOut &&
                            <ErrorMessage
                                title="This conversation has expired"
                                message={
                                    `To continue sending a message to ${hipaaSafePracticeName}, please enter your five-digit PIN number.
                                    If you have any immediate concerns, please contact ${hipaaSafePracticeName}${additionalPracticePhoneCopy}`
                                }
                            />
                        }

                        {!lockedOut &&
                            <div className={cx('formGroup', s.verticalSpacing)}>
                                <div className={cx(s.componentBlock, 'fullWidth')}>
                                    <input
                                        id="PIN"
                                        name="PIN"
                                        type="number"
                                        pattern="[0-9]*"
                                        className={cx('inputControl', s.bigInput, s.fauxPassword)}
                                        maxLength="5"
                                        onChange={this.handleChangePIN}
                                        onKeyDown={this.handleFloatingPointKeys}
                                        value={PIN}
                                        required
                                    />
                                    <label
                                        htmlFor="PIN"
                                        className={s.heading}
                                    >
                                        <span>PIN</span>
                                    </label>
                                </div>
                            </div>
                        }

                        {session.error && hasConversationDetails &&
                            <ErrorMessage
                                title="Failure to authenticate"
                                message={`${session.error.message} ${lockedOut ? additionalLockedOutCopy : ''} If you have any questions or concerns, please contact ${hipaaSafePracticeName}${additionalPracticePhoneCopy}`}
                            />
                        }

                        {!lockedOut &&
                            <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                                <button
                                    className={cx(s.btn, s.primary)}
                                    id="submitLogin"
                                    type="submit"
                                    disabled={!isValidPIN || isLoadingLimitedLogin}
                                >
                                    {isLoadingLimitedLogin ?
                                        <LoadingState context="loginForm"/> :
                                        <span className={s.btnText}>Verify</span>
                                    }
                                </button>
                            </div>
                        }
                        <Footer/>
                    </form>
                }

                {/* Patient initiated conversation ui. */}
                {session.limitedLogin && hasConversationDetails && !lockedOut &&
                    <form
                        className={cx(s.component, messages.error ? 'hasErrors' : '')}
                        id="limitedLoginSendMessage"
                        onSubmit={this.handleLimitedLoginMessageSend}
                    >
                        <input type="hidden" value="trickChromesAutoCompleteBullshit" />

                        <div id="conversationDetails" className={cx(s.alignItems, s.componentBlock, s.verticalSpacing, s.heading)}>
                            <h3>{conversationDetails.practiceName}</h3>
                            <label>{conversationDetails.practicePhone}</label>
                        </div>

                        {!limitedLoginSendMessageTimerEnd && !limitedLoginMessageSentSuccess &&
                            <React.Fragment>
                                <ErrorMessage
                                    title="This conversation has expired"
                                    message={`PIN verified successfully! Use the form below to send a message to ${hipaaSafePracticeName}.`}
                                />
                                <div className={cx('formGroup', s.verticalSpacing)}>
                                    <label className="hideForWcag" htmlFor="messageInput">Enter message</label>
                                    <textarea
                                        autoComplete="messageInput"
                                        id="messageInput"
                                        name="messageInput"
                                        disabled={isLoadingLimitedLoginSendMessage}
                                        value={input}
                                        onChange={this.inputHandler}
                                        className="inputControl"
                                        placeholder="Enter message"
                                        maxLength={4000}
                                        required
                                    />
                                </div>
                            </React.Fragment>
                        }

                        {limitedLoginMessageSentSuccess &&
                            <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                                <p>Your message has been sent. Thank you!</p>
                            </div>
                        }

                        {/* Limited login send message ui when session.time ends. */}
                        {limitedLoginSendMessageTimerEnd && !limitedLoginMessageSentSuccess &&
                            <React.Fragment>
                                <ErrorMessage
                                    title="Oops, it took too long to send a message."
                                    message={`Please try again if you would like to send a message to ${hipaaSafePracticeName}.`}
                                />

                                <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                                    <Link
                                        className={s.link}
                                        to={`/verify/${conversationHash}`}
                                    >
                                        Try again?
                                    </Link>
                                </div>
                            </React.Fragment>
                        }

                        {messages.error &&
                            <ErrorMessage
                                title="Failure to send message"
                                message={`If you have any questions or concerns, please contact ${hipaaSafePracticeName}${additionalPracticePhoneCopy}`}
                            />
                        }

                        {!limitedLoginSendMessageTimerEnd && !limitedLoginMessageSentSuccess &&
                            <div className={cx(s.alignItems, s.componentBlock, s.verticalSpacing)}>
                                <button
                                    className={cx(s.btn, s.primary)}
                                    id="submitLimitedLoginMessage"
                                    type="submit"
                                    disabled={!isValidMessage || isLoadingLimitedLoginSendMessage}
                                >
                                    {isLoadingLimitedLoginSendMessage ?
                                        <LoadingState context="loginForm"/> :
                                        <span className={s.btnText}>Send Secure Message</span>
                                    }
                                </button>
                            </div>
                        }

                        {!messages.error &&
                            <label className={s.heading}>
                                <em>
                                    <small>
                                        If you have any questions or concerns, please contact {hipaaSafePracticeName} {additionalPracticePhoneCopy}
                                    </small>
                                </em>
                            </label>
                        }
                        <Footer/>
                    </form>
                }
            </Layout>
        );
    }
}